import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, EmptyState, Skeleton } from '@/ui';
import { adminErrorMessage } from './error-message';
import styles from './Admin.module.css';

/**
 * LISTE DURUMU — dort ekranin da ayni sekilde cizdigi uc hal.
 *
 * Ayri bir bilesen olmasinin sebebi tekrarin kisaltilmasi DEGIL, uc halin
 * BIRBIRINDEN AYRI kalmasi:
 *
 *   yukleniyor    -> iskelet
 *   HATA          -> mesaj + TEKRAR DENE
 *   kayit yok     -> notr bos durum, tekrar deneme YOK
 *
 * "Kayit yok" ile "yaniti anlayamadim" ayni ekrani gosterirse, sozlesme
 * ayrismasi (ApiShapeError) sessizce "hic kullanici yok" diye okunur. Bu
 * yuzden api.ts beklenmeyen sekilde firlatiyor ve buradaki hata dali onu
 * TEKRAR DENENEBILIR bir hata olarak gosteriyor.
 */
export type ListStatusProps = {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  /** Sorgu basarili ve HIC satir yok. */
  isEmpty: boolean;
  emptyTitle: string;
  emptyIcon?: ReactNode;
  emptyDescription?: string;
  onRetry: () => void;
};

export function ListStatus({
  isPending,
  isError,
  error,
  isEmpty,
  emptyTitle,
  emptyIcon,
  emptyDescription,
  onRetry,
}: ListStatusProps) {
  const { t } = useTranslation();

  if (isPending) {
    return (
      <Card padding="lg">
        <Skeleton shape="block" height="240px" />
      </Card>
    );
  }

  if (isError) {
    return (
      <Card padding="lg">
        <EmptyState
          tone="error"
          title={t('list.error.title')}
          description={adminErrorMessage(error, t)}
          action={
            <Button tone="neutral" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          }
        />
      </Card>
    );
  }

  if (isEmpty) {
    return (
      <Card padding="lg">
        <EmptyState
          icon={emptyIcon}
          title={emptyTitle}
          {...(emptyDescription === undefined ? {} : { description: emptyDescription })}
        />
      </Card>
    );
  }

  return null;
}

/** Sayfalama kuyrugu — `total` YOK, sayac cizilemez; yalniz `hasMore`. */
export function LoadMore({
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className={styles.loadMore}>
      {hasNextPage ? (
        <Button tone="neutral" loading={isFetchingNextPage} onClick={onLoadMore}>
          {t('admin.loadMore')}
        </Button>
      ) : (
        <span className={styles.muted}>{t('list.endOfList')}</span>
      )}
    </div>
  );
}
